<template>
  <v-row id="watch-form">
    <template v-if="myPosition && myPosition.type">
      <v-col v-if="!editing" cols="12" md="12" class="my-0 py-0">
        <div class="mt-3">
          <v-chip-group v-model="myPosition.type" active-class="primary" mandatory show-arrows>
            <v-chip
              v-for="positionTypeElem in positionTypes"
              :key="positionTypeElem.value"
              :value="positionTypeElem.value"
              outlined
            >
              {{ positionTypeElem.text }}
            </v-chip>
          </v-chip-group>
        </div>
      </v-col>

      <template
        v-if="['stock', 'fund', 'etf', 'plan', 'bond', 'index'].indexOf(myPosition.type) > -1"
      >
        <v-col cols="12" :md="showCountrySelector ? 4 : 9">
          <div class="items-flex">
            <v-text-field
              v-model="myPosition.ticker"
              :label="tickerLabelTranslation"
              :disabled="loading"
              maxLength="30"
              hide-details
            />
            <span class="ml-2">
              <InfoIcon :size="15" :text="tickerLabelHelp" />
            </span>
          </div>
        </v-col>
        <v-col v-if="showCountrySelector" cols="12" md="5">
          <div class="items-flex">
            <CountrySelector
              v-model="myPosition.country"
              :countries="countrySelectorList"
              :label="$t('views.stocks.stockModal.country')"
              translate-name
              :disabled="loading"
              hide-details
            />
            <span class="ml-2">
              <InfoIcon
                :size="15"
                :text="$t(`views.stocks.stockModal.countryHelp.${myPosition.type}`)"
              />
            </span>
          </div>
        </v-col>
        <v-col cols="12" md="3">
          <CurrencySelector
            v-model="myPosition.currency"
            :disabled="loading || editing"
            model="position"
            :label="$t('views.stocks.stockModal.currency')"
          />
        </v-col>
      </template>
      <template v-if="myPosition.type === 'crypto'">
        <v-col cols="12" md="12">
          <div class="items-flex">
            <v-text-field
              v-model="myPosition.ticker"
              :label="tickerLabelTranslation"
              :disabled="loading"
              maxLength="30"
              hide-details
            />
            <span class="ml-2">
              <InfoIcon :size="15" :text="tickerLabelHelp" />
            </span>
          </div>
        </v-col>
      </template>
      <template v-if="myPosition.type === 'commodity'">
        <v-col cols="12" md="12">
          <v-autocomplete
            v-model="myPosition.ticker"
            :items="commodityPositionTickers"
            :disabled="loading || editing"
            :label="$t('views.stocks.stockModal.commodityPosition.commodityPosition')"
            :placeholder="$t('views.stocks.stockModal.commodityPosition.commodityPosition')"
            hide-no-data
            hide-details
          ></v-autocomplete>
        </v-col>
      </template>
      <template v-if="myPosition.type === 'cash'">
        <v-col cols="12" md="12">
          <v-autocomplete
            v-model="myPosition.ticker"
            :items="cashPositionTickers"
            :disabled="loading || editing"
            :label="$t('views.stocks.stockModal.cashPosition.currency')"
            :placeholder="$t('views.stocks.stockModal.cashPosition.currency')"
            hide-no-data
            hide-details
          ></v-autocomplete>
        </v-col>
      </template>

      <v-col
        v-if="showCountrySelector && exchangeSelectorList && exchangeSelectorList.length"
        cols="12"
        md="12"
      >
        <div class="items-flex">
          <v-select
            v-model="myPosition.exchange"
            :items="exchangeSelectorList"
            :label="$t('views.stocks.stockModal.exchange.label')"
            :disabled="loading"
            hide-details
          ></v-select>
          <span class="ml-2">
            <InfoIcon :size="15" :text="$t('views.stocks.stockModal.exchange.help')" />
          </span>
        </div>
      </v-col>

      <v-col v-if="myPosition.type !== 'cash'" cols="12" :md="showSector ? 7 : 12">
        <v-text-field
          v-model="myPosition.name"
          :label="$t('views.stocks.stockModal.name')"
          :disabled="loading"
          hide-details
          maxLength="100"
        />
      </v-col>

      <v-col v-if="showSector" cols="12" md="5">
        <SectorBox
          v-model="myPosition.sector"
          group="watch"
          :label="$t('views.stocks.stockModal.sector')"
          :disabled="loading"
        />
      </v-col>

      <v-col
        v-if="['fund', 'etf', 'plan'].indexOf(positionOrDerivativePositionType) > -1"
        cols="12"
        md="12"
        class="pt-2"
      >
        <v-text-field
          v-model="myPosition.manager"
          :label="$t('views.stocks.stockModal.manager')"
          :disabled="loading"
          hide-details
          maxLength="100"
        />
      </v-col>

      <v-col v-if="myPosition.type !== 'cash'" cols="12" md="12" class="mb-0 pb-0">
        <TagBox
          v-model="myPosition.tags"
          group="watch"
          :disabled="loading"
          :label="$t('views.stocks.stockModal.tags.label')"
          :hint="$t('views.stocks.stockModal.tags.hint')"
        />
      </v-col>

      <v-col cols="12" md="12" class="mb-0 pb-0">
        <h2 class="mt-3">
          <v-icon>mdi-bullseye-arrow</v-icon>
          <span class="ml-2">{{ $t('views.watchList.watchModal.watchForm.targets') }}</span>
        </h2>
      </v-col>

      <v-col cols="12" md="12" class="mt-1 pt-1 mb-0 pb-0">
        <v-switch v-model="showBidPrice" :disabled="loading" hide-details>
          <template v-slot:label>
            <span class="mr-2">
              {{ $t(`views.watchList.watchModal.watchForm.bidPrice.show`) }}
            </span>
            <InfoIcon :text="$t(`views.watchList.watchModal.watchForm.bidPrice.info`)" />
          </template>
        </v-switch>
      </v-col>
      <template v-if="showBidPrice">
        <v-col cols="12" md="6" class="pt-2 mb-0 pb-0">
          <CurrencyInput
            v-model="myPosition.targets.bidPrice.price"
            :currency="myPosition.currency"
            :label="$t('views.watchList.watchModal.watchForm.bidPrice.label')"
            :disabled="loading"
            prependInnerIcon="mdi-arrow-up-circle"
          />
        </v-col>
        <!-- <v-col cols="12" md="6" class="mb-0 pb-0">
          <v-chip-group
            v-model="myPosition.targets.bidPrice.notifications"
            active-class="primary"
            class="pa-0 ma-0 mt-1"
            multiple
            column
          >
            <v-chip filter outlined value="bell">
              <v-avatar left>
                <v-icon size="15">mdi-bell</v-icon>
              </v-avatar>
              {{ $t(`views.watchList.watchModal.watchForm.notification`) }}
            </v-chip>
            <v-chip filter outlined value="email">
              <v-avatar left>
                <v-icon size="15">mdi-email</v-icon>
              </v-avatar>
              Email
            </v-chip>
          </v-chip-group>
        </v-col> -->
      </template>

      <v-col cols="12" md="12" class="mb-0 pb-0">
        <v-switch v-model="showAskPrice" :disabled="loading" hide-details>
          <template v-slot:label>
            <span class="mr-2">
              {{ $t(`views.watchList.watchModal.watchForm.askPrice.show`) }}
            </span>
            <InfoIcon :text="$t(`views.watchList.watchModal.watchForm.askPrice.info`)" />
          </template>
        </v-switch>
      </v-col>
      <template v-if="showAskPrice">
        <v-col cols="12" md="6" class="mb-0 pb-0">
          <CurrencyInput
            v-model="myPosition.targets.askPrice.price"
            :currency="myPosition.currency"
            :label="$t('views.watchList.watchModal.watchForm.askPrice.label')"
            :disabled="loading"
            prependInnerIcon="mdi-arrow-down-circle"
          />
        </v-col>
        <!-- <v-col cols="12" md="6" class="mb-0 pb-0">
          <v-chip-group
            v-model="myPosition.targets.askPrice.notifications"
            active-class="primary"
            class="pa-0 ma-0 mt-1"
            multiple
            column
          >
            <v-chip filter outlined value="bell">
              <v-avatar left>
                <v-icon size="15">mdi-bell</v-icon>
              </v-avatar>
              {{ $t(`views.watchList.watchModal.watchForm.notification`) }}
            </v-chip>
            <v-chip filter outlined value="email">
              <v-avatar left>
                <v-icon size="15">mdi-email</v-icon>
              </v-avatar>
              Email
            </v-chip>
          </v-chip-group>
        </v-col> -->
      </template>

      <v-col cols="12" md="12">
        <v-text-field
          v-model="myPosition.notes"
          :label="$t(`views.watchList.watchModal.watchForm.notes`)"
          :disabled="loading"
          hide-details
          maxLength="500"
        />
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { get } from 'lodash'
import PositionFormMixin from '@/views/components/common/positions/PositionFormMixin'

export default {
  mixins: [PositionFormMixin],
  data: () => ({
    type: 'watch',
    showBidPrice: false,
    showAskPrice: false,
  }),
  watch: {
    showBidPrice: {
      handler() {
        if (!this.showBidPrice) {
          this.$set(this.myPosition.targets, 'bidPrice', { notifications: [] })
        }
      },
    },
    showAskPrice: {
      handler() {
        if (!this.showAskPrice) {
          this.$set(this.myPosition.targets, 'askPrice', { notifications: [] })
        }
      },
    },
    'myPosition.targets': {
      handler() {
        if (get(this.myPosition, 'targets.bidPrice.price', null)) {
          this.showBidPrice = true
        }
        if (get(this.myPosition, 'targets.askPrice.price', null)) {
          this.showAskPrice = true
        }
      },
      deep: true,
      immediate: true,
    },
  },
}
</script>

<style lang="scss">
#watch-form {
  h2 {
    font-size: 14px;
    font-weight: 400;
  }
  .items-flex {
    display: flex;
    align-items: flex-end;
  }
}
</style>
